/*
 * Web app root — routing only. main.tsx hydrates auth before rendering routes.
 *
 * Public routes:
 *   /            → Landing (marketing)
 *   /privacy     → Privacy policy
 *   /support     → Support page
 *   /app/login   → Login screen
 *
 * Authed routes (gated by <AuthGate>):
 *   /app         → redirect to /app/home
 *   /app/home    → Home (placeholder for ported mobile Home)
 */

import { Routes, Route, Navigate } from 'react-router-dom';

import Landing from '@/routes/Landing';
import Privacy from '@/routes/Privacy';
import Support from '@/routes/Support';
import Login from '@/routes/Login';
import Register from '@/routes/Register';
import ForgotPassword from '@/routes/ForgotPassword';
import ResetPassword from '@/routes/ResetPassword';
import JoinTournament from '@/routes/JoinTournament';
import PendingTournamentInvitations from '@/routes/PendingTournamentInvitations';
import Home from '@/routes/Home';
import Profile from '@/routes/Profile';
import TournamentsList from '@/routes/tournaments/TournamentsList';
import CreateTournament from '@/routes/tournaments/CreateTournament';
import TournamentDetail from '@/routes/tournaments/TournamentDetail';
import EditTournament from '@/routes/tournaments/EditTournament';
import AddTournamentPlayer from '@/routes/tournaments/AddTournamentPlayer';
import TeamSetup from '@/routes/tournaments/TeamSetup';
import TeamBranding from '@/routes/tournaments/TeamBranding';
import Players from '@/routes/tournaments/Players';
import RoundsList from '@/routes/tournaments/RoundsList';
import CreateRound from '@/routes/tournaments/CreateRound';
import EditRound from '@/routes/tournaments/EditRound';
import RoundDetail from '@/routes/tournaments/RoundDetail';
import DraftRoom from '@/routes/tournaments/DraftRoom';
import { useParams } from 'react-router-dom';
import LeaderboardBroadcast from '@/routes/tournaments/LeaderboardBroadcast';
import MatchScoring from '@/routes/tournaments/MatchScoring';
import TeamStablefordScoring from '@/routes/tournaments/TeamStablefordScoring';
import MyMatchesPage from '@/routes/tournaments/MyMatchesPage';
import ScorecardSetup from '@/routes/ScorecardSetup';
import ScorecardRound from '@/routes/ScorecardRound';
import RoundMatchesView from '@/routes/tournaments/RoundMatchesView';
import TeamsOverview from '@/routes/tournaments/TeamsOverview';
import Recap from '@/routes/tournaments/Recap';
import { AuthGate } from '@/components/AuthGate';

/** Old /leaderboard deep links bounce to the Teams tab, which is the
 *  live-scoring surface now. */
function LeaderboardRedirect() {
  const { eventId } = useParams<{ eventId: string }>();
  return <Navigate to={`/app/tournaments/${eventId}/teams`} replace />;
}

export default function App() {
  return (
    <Routes>
      {/* ── Public marketing ───────────────────────────────────────── */}
      <Route path="/" element={<Landing />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/support" element={<Support />} />

      {/* ── App entry ─────────────────────────────────────────────── */}
      <Route path="/app/login" element={<Login />} />
      <Route path="/app/register" element={<Register />} />
      <Route path="/app/forgot-password" element={<ForgotPassword />} />
      <Route path="/app/reset-password" element={<ResetPassword />} />
      {/* Public route: accessible without auth so a tap on the share link
          can show the event preview. Login is prompted only on Join click. */}
      <Route path="/app/join/:code" element={<JoinTournament />} />
      <Route
        path="/app/invitations"
        element={<AuthGate><PendingTournamentInvitations /></AuthGate>}
      />
      <Route path="/app" element={<Navigate to="/app/home" replace />} />
      <Route
        path="/app/home"
        element={
          <AuthGate>
            <Home />
          </AuthGate>
        }
      />

      <Route path="/app/profile" element={<AuthGate><Profile /></AuthGate>} />

      {/* ── Core scorecards ─────────────────────────────────────────── */}
      <Route path="/app/scorecards/new" element={<AuthGate><ScorecardSetup /></AuthGate>} />
      <Route path="/app/scorecards/:roundId" element={<AuthGate><ScorecardRound /></AuthGate>} />

      {/* ── Tournaments ───────────────────────────────────────────── */}
      <Route
        path="/app/tournaments"
        element={
          <AuthGate>
            <TournamentsList />
          </AuthGate>
        }
      />
      <Route
        path="/app/tournaments/new"
        element={
          <AuthGate>
            <CreateTournament />
          </AuthGate>
        }
      />
      <Route
        path="/app/tournaments/:eventId"
        element={
          <AuthGate>
            <TournamentDetail />
          </AuthGate>
        }
      />
      <Route
        path="/app/tournaments/:eventId/edit"
        element={
          <AuthGate>
            <EditTournament />
          </AuthGate>
        }
      />
      <Route
        path="/app/tournaments/:eventId/players/add"
        element={
          <AuthGate>
            <AddTournamentPlayer />
          </AuthGate>
        }
      />
      {/* Teams tab landing — broadcast-style team matches overview. */}
      <Route
        path="/app/tournaments/:eventId/teams"
        element={
          <AuthGate>
            <TeamsOverview />
          </AuthGate>
        }
      />
      {/* Roster + captain management lives under /teams/manage. The
          Teams tab landing surfaces a "Manage rosters" link to here for
          owners and captains. */}
      <Route
        path="/app/tournaments/:eventId/teams/manage"
        element={
          <AuthGate>
            <TeamSetup />
          </AuthGate>
        }
      />
      <Route
        path="/app/tournaments/:eventId/teams/:teamId/branding"
        element={
          <AuthGate>
            <TeamBranding />
          </AuthGate>
        }
      />
      <Route path="/app/tournaments/:eventId/players" element={<AuthGate><Players /></AuthGate>} />
      <Route path="/app/tournaments/:eventId/rounds" element={<AuthGate><RoundsList /></AuthGate>} />
      <Route path="/app/tournaments/:eventId/rounds/new" element={<AuthGate><CreateRound /></AuthGate>} />
      <Route path="/app/tournaments/:eventId/rounds/:roundId" element={<AuthGate><RoundDetail /></AuthGate>} />
      <Route path="/app/tournaments/:eventId/rounds/:roundId/edit" element={<AuthGate><EditRound /></AuthGate>} />
      <Route path="/app/tournaments/:eventId/draft" element={<AuthGate><DraftRoom /></AuthGate>} />
      {/* Live Scoring page retired — the Teams tab absorbed its unique
          pieces (TV Mode link, clinch progress bar, player records).
          Old links land on Teams. TV Mode itself lives on. */}
      <Route path="/app/tournaments/:eventId/leaderboard" element={<LeaderboardRedirect />} />
      <Route path="/app/tournaments/:eventId/leaderboard/tv" element={<AuthGate><LeaderboardBroadcast /></AuthGate>} />
      <Route path="/app/tournaments/:eventId/recap" element={<AuthGate><Recap /></AuthGate>} />
      <Route path="/app/tournament-matches/:matchId/score" element={<AuthGate><MatchScoring /></AuthGate>} />
      <Route path="/app/tournament-rounds/:roundId/stableford" element={<AuthGate><TeamStablefordScoring /></AuthGate>} />
      <Route path="/app/tournament-rounds/:roundId/matches" element={<AuthGate><RoundMatchesView /></AuthGate>} />
      <Route path="/app/tournaments/:eventId/my-matches" element={<AuthGate><MyMatchesPage /></AuthGate>} />

      {/* ── Legacy .html paths from the previous deploy → SPA routes ── */}
      <Route path="/index.html" element={<Navigate to="/" replace />} />
      <Route path="/privacy.html" element={<Navigate to="/privacy" replace />} />
      <Route path="/support.html" element={<Navigate to="/support" replace />} />

      {/* ── 404 → bounce to landing ───────────────────────────────── */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
